import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../store/hooks';
import { setActiveRightTab } from '../store/slices/planningSlice';
import { captureDesignEditorScroll } from '../utils/designEditorScroll';
import { isIOSApp } from '../utils/platformDetection';

import { IOS_RESTRICTED_TABS, RightPanelTab, VALID_TAB_KEYS } from './PlanningPageHelpers';
import { buildDesignEditorNavigationState, buildDesignEditorPath } from './planningRouteUtils';

interface PlanningPageRouting {
  /** Navigate to a tab, optionally with an item ID */
  navigateToTab: (
    tab: RightPanelTab,
    itemId?: string | null,
    options?: { replace?: boolean },
  ) => void;
  /** Navigate to a chat session */
  navigateToSession: (sessionId: string) => void;
  /** Open one generated page directly in the visual editor. */
  navigateToDesignEditor: (pageName: string) => void;
  /** The chat session ID parsed from the URL, or null */
  routeSessionId: string | null;
  /** The item ID parsed from the URL (e.g. task ID), or null */
  selectedItemId: string | null;
}

type ParsedPlanningPath = {
  sessionId: string | null;
  tab: RightPanelTab;
  itemId: string | null;
};

const DEFAULT_PATH_RESULT: ParsedPlanningPath = {
  sessionId: null,
  tab: 'ai-overview',
  itemId: null,
};

const IOS_DESIGN_SCROLL_TARGET = { selector: '[data-artifact="design"]', label: 'Design', tabKey: 'design' };

const scrollToDesignArtifact = (delay = 120) => {
  globalThis.setTimeout(() => {
    window.dispatchEvent(new globalThis.CustomEvent('scroll_to_artifact', {
      detail: IOS_DESIGN_SCROLL_TARGET,
    }));
  }, delay);
};

const showIosRestriction = () => {
  window.dispatchEvent(new globalThis.CustomEvent('ios_app_store_restriction'));
};

const getIosSafeTab = (tab: RightPanelTab): RightPanelTab => (
  isIOSApp() && (tab === 'design' || IOS_RESTRICTED_TABS.has(tab)) ? 'ai-overview' : tab
);

const blockIosRestrictedTab = (tab: RightPanelTab): boolean => {
  if (!isIOSApp() || !IOS_RESTRICTED_TABS.has(tab)) return false;

  showIosRestriction();

  return true;
};

const getNavigationTarget = (tab: RightPanelTab, itemId?: string | null) => {
  const iosDesign = isIOSApp() && tab === 'design';
  const effectiveTab = getIosSafeTab(tab);

  return {
    effectiveTab,
    itemId: iosDesign ? null : itemId,
    scrollToDesign: iosDesign,
  };
};

/** URL slug aliases → canonical tab keys (e.g. "userflow" → "user-flow") */
const TAB_ALIASES: Record<string, RightPanelTab> = {
  pages: 'design',
  userflow: 'user-flow',
};

const parseTabSegments = (segments: string[]): Pick<ParsedPlanningPath, 'tab' | 'itemId'> => {
  const raw = segments[0] ?? '';
  const tabSegment = (TAB_ALIASES[raw] ?? raw) as RightPanelTab;

  if (tabSegment && VALID_TAB_KEYS.has(tabSegment)) {
    return { tab: tabSegment, itemId: segments.length > 1 ? segments.slice(1).join('/') : null };
  }

  return { tab: DEFAULT_PATH_RESULT.tab, itemId: DEFAULT_PATH_RESULT.itemId };
};

const parseSegments = (pathname: string, prefix: string): ParsedPlanningPath => {
  const segments = pathname.slice(prefix.length).split('/').filter(Boolean);
  const first = segments[0] ?? '';
  const firstAsTab = (TAB_ALIASES[first] ?? first) as RightPanelTab;

  if (!first) return DEFAULT_PATH_RESULT;
  if (VALID_TAB_KEYS.has(firstAsTab)) return { sessionId: null, ...parseTabSegments(segments) };

  return {
    sessionId: first,
    ...parseTabSegments(segments.slice(1)),
  };
};

/**
 * Derive the active session, tab and optional item ID from the URL pathname.
 * Expected formats:
 *   /planning/:projectId/:sessionId?
 *   /planning/:projectId/:sessionId?/:tab/:itemId?
 *   /planning/:projectId/:tab/:itemId?
 */
const parseTabFromPath = (
  pathname: string,
  projectId: string | undefined
): ParsedPlanningPath => {
  if (!projectId) return DEFAULT_PATH_RESULT;

  const prefix = `/planning/${projectId}/`;

  return pathname.startsWith(prefix) ? parseSegments(pathname, prefix) : DEFAULT_PATH_RESULT;
};

const buildSessionPlanningPath = (
  projectId: string,
  sessionId: string | null,
  targetTab: RightPanelTab,
  targetItemId?: string | null,
): string => {
  const basePath = sessionId ? `/planning/${projectId}/${sessionId}` : `/planning/${projectId}`;

  if (targetTab === 'ai-overview' && !targetItemId) return basePath;
  if (targetItemId) return `${basePath}/${targetTab}/${targetItemId}`;

  return `${basePath}/${targetTab}`;
};

const useSyncActiveRightTab = (
  projectId: string | undefined,
  sessionId: string | null,
  tab: RightPanelTab,
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (projectId && isIOSApp() && (tab === 'design' || IOS_RESTRICTED_TABS.has(tab))) {
      navigate(buildSessionPlanningPath(projectId, sessionId, 'ai-overview'), { replace: true });
      dispatch(setActiveRightTab('ai-overview'));
      if (tab === 'design') scrollToDesignArtifact();
      else showIosRestriction();

      return;
    }
    dispatch(setActiveRightTab(tab));
  }, [dispatch, navigate, projectId, sessionId, tab]);
};

const useNavigateToTab = (
  projectId: string | undefined,
  sessionId: string | null,
): PlanningPageRouting['navigateToTab'] => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  return useCallback(
    (
      targetTab: RightPanelTab,
      targetItemId?: string | null,
      options?: { replace?: boolean },
    ) => {
      if (!projectId || blockIosRestrictedTab(targetTab)) return;

      const target = getNavigationTarget(targetTab, targetItemId);

      const path = buildSessionPlanningPath(projectId, sessionId, target.effectiveTab, target.itemId);
      const preserveEditorMode = target.effectiveTab === 'design'
        && Boolean(target.itemId?.startsWith('pages/'))
        && new URLSearchParams(location.search).get('mode') === 'edit';

      navigate(`${path}${preserveEditorMode ? location.search : ''}`, {
        replace: options?.replace === true,
      });
      dispatch(setActiveRightTab(target.effectiveTab));
      if (target.scrollToDesign) scrollToDesignArtifact();
    },
    [projectId, sessionId, navigate, dispatch, location.search]
  );
};

const useNavigateToSession = (
  projectId: string | undefined
): PlanningPageRouting['navigateToSession'] => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useCallback((targetSessionId: string) => {
    if (!projectId) return;

    navigate(`/planning/${projectId}/${encodeURIComponent(targetSessionId)}`);
    dispatch(setActiveRightTab('ai-overview'));
  }, [projectId, navigate, dispatch]);
};

const useNavigateToDesignEditor = (
  projectId: string | undefined,
  sessionId: string | null,
): PlanningPageRouting['navigateToDesignEditor'] => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  return useCallback((pageName: string) => {
    if (!projectId || !pageName || blockIosRestrictedTab('design')) return;
    navigate(buildDesignEditorPath(projectId, sessionId, pageName), {
      state: buildDesignEditorNavigationState(location, captureDesignEditorScroll()),
    });
    dispatch(setActiveRightTab('design'));
  }, [dispatch, location, navigate, projectId, sessionId]);
};

/**
 * Syncs the URL with the active right-panel tab on the Planning page.
 * On mount / URL change ➜ dispatches the tab to Redux.
 * Exposes navigateToTab() for programmatic navigation.
 */
export const usePlanningPageRouting = (
  projectId: string | undefined
): PlanningPageRouting => {
  const pathname = useLocation().pathname;

  const { sessionId, tab, itemId } = useMemo(
    () => parseTabFromPath(pathname, projectId),
    [pathname, projectId]
  );
  const navigateToTab = useNavigateToTab(projectId, sessionId);
  const navigateToSession = useNavigateToSession(projectId);
  const navigateToDesignEditor = useNavigateToDesignEditor(projectId, sessionId);

  useSyncActiveRightTab(projectId, sessionId, tab);

  return {
    navigateToTab,
    navigateToSession,
    navigateToDesignEditor,
    routeSessionId: sessionId,
    selectedItemId: itemId,
  };
};
